import { Card, Button, ProgressBar } from "react-bootstrap";
import "./assessment-card.scss";
import { useNavigate } from "react-router";
import {
  UxAvatar,
  UxAvatarEnum,
  UxAvatarPriorityEnum,
  UxAvatarStyleEnum,
  UxSizeExtendedEnum
} from "@imaginelearning/mps-ux-component-library";
import useUser from "hooks/useuser";
import { formatAssessmentDueDate } from "helpers/formatter";
import { ArrowSvg, Icon } from "../growbot/icons";
import { SubjectIcon } from "../svg-images/subjects/subject-icon";
import { IAssessment, sessionStorageKeys } from "../types";
import { getSubjectName } from "./getsubjectname";
import { useAssessments } from "../context/assessment-provider";

interface IAssessmentCardProps extends IAssessment {
  viewType: string;
}

function getFormattedDueDate(dueDate: string, viewType: string, userTimeZone?: string) {
  const formattedDate = formatAssessmentDueDate(dueDate, userTimeZone);
  return viewType === "grade3plus" ? <strong>{formattedDate}</strong> : formattedDate;
}

function AssessmentCard(props: IAssessmentCardProps) {
  const {
    id,
    viewType,
    title,
    dueDate,
    subject,
    assessmentType,
    progress = 0,
    icon,
    disabled,
    testNumber,
    isRetake
  } = props;
  const navigate = useNavigate();
  const { launchDiagnostic, launchScreener } = useAssessments();
  const { user } = useUser();

  const selectedIcon = !icon || icon === "" ? null : (UxAvatarEnum as any)[icon.toUpperCase()];

  const handleButtonClick = () => {
    switch (assessmentType) {
      case "formative":
        navigate(`/assessments/formatives/${id}`);
        break;
      case "diagnostic":
        sessionStorage.setItem(sessionStorageKeys.diagnosticSubject, subject);
        sessionStorage.setItem(sessionStorageKeys.diagnosticTitle, title);
        navigate("/assessments/diagnostic-return?incomplete=true", { replace: true });
        launchDiagnostic(testNumber ?? 1, false);
        break;
      case "screener":
        launchScreener(testNumber ?? 1, !!isRetake);
        break;
      default:
        console.warn("Unexpected assessment type on assessment card", { assessmentType, title });
        break;
    }
  };

  return (
    <Card
      className={`assessment-card ${assessmentType} ${disabled && "disabled-card"}`}
      aria-label={`Open ${title} details`}
    >
      <div className="card-image">
        <SubjectIcon subject={subject} />
        <div className="assessment-subject">{getSubjectName(subject)}</div>
      </div>
      <Card.Body>
        <div className="badge" role="status" aria-label={`Card type: ${assessmentType}`}>
          {assessmentType}
        </div>
        <div className="card-main">
          <h3 className="ellipsis">{title}</h3>
        </div>
        <div className="button-container">
          {viewType === "grade3plus" && assessmentType === "formative" && (
            <div className={`assessment-icon ${assessmentType}`}>
              {selectedIcon && (
                <UxAvatar
                  avatar={selectedIcon as UxAvatarEnum}
                  avatarType={UxAvatarPriorityEnum.DEFAULT}
                  avatarStyle={UxAvatarStyleEnum.BODY}
                  className="d-flex assessment_avatar"
                />
              )}
            </div>
          )}
          {viewType === "gradekto2" && (
            <Button
              variant={assessmentType}
              aria-label={`Go to ${title}`}
              disabled={!!disabled}
              onClick={handleButtonClick}
            >
              <span>GO</span> <ArrowSvg />
            </Button>
          )}
          {viewType === "grade3plus" && (
            <Button
              variant={assessmentType}
              aria-label={`Go to ${title}`}
              disabled={!!disabled}
              onClick={handleButtonClick}
            >
              <span className="pt-1">Start</span>
            </Button>
          )}
        </div>

        <div className="due-date" aria-live="polite">
          {dueDate && !dueDate.includes("9999") && (
            <>
              <strong>Due Date:</strong> {getFormattedDueDate(dueDate, viewType, user?.organization?.timezone)}
            </>
          )}
        </div>
        {viewType === "grade3plus" && assessmentType === "formative" && (
          <ProgressBar now={progress} style={{ width: "80%" }} />
        )}
      </Card.Body>

      {viewType === "gradekto2" && assessmentType !== "formative" && (
        <div className={`card-icon ${assessmentType}`}>
          <Icon type={assessmentType} disabled={disabled} />
        </div>
      )}
      {viewType === "gradekto2" && assessmentType === "formative" && (
        <div data-testid="icon-formative" className={`card-icon ${assessmentType}`}>
          {selectedIcon && (
            <UxAvatar
              avatar={selectedIcon as UxAvatarEnum}
              avatarType={UxAvatarPriorityEnum.DEFAULT}
              avatarStyle={UxAvatarStyleEnum.BODY}
              size={UxSizeExtendedEnum.X2}
              className="d-flex"
            />
          )}
        </div>
      )}
    </Card>
  );
}

export default AssessmentCard;
